// libs
import React, { useState, useRef } from 'react';
import { useForm, Controller } from "react-hook-form";
import { ToastContainer, toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faCopy, faDownload } from '@fortawesome/free-solid-svg-icons';
import "../../assets/css/form.css";
import "react-datepicker/dist/react-datepicker.css";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
// components 
import Loading from '../../components/loading';
import JsonNodeEditor from '../../components/config-builder/json-node-editor';
// config & functions 
import ajaxCall from '../../utils/ajaxcall';
import config from "../../config/index";
import { getConfigType, isPageUrlHidden } from './config-types';

function FormModal({loadapidata, apidataparams, setshowformmodalbox, urlaccountid, urlconfigtype, rowid, token, orginfo}) {

    const [InputFields, setInputFields] = useState({
        accountId: "",
        statusId: "",
        rowId: "",
        pageUrl: "",
        pageConfig: (!rowid && getConfigType(urlconfigtype)?.template) ? JSON.stringify(getConfigType(urlconfigtype).template, null, 2) : ""
    });
    const disabledField = (orginfo.roleId === 1 || orginfo.roleId === 2) ? false:true;
    const configType = getConfigType(urlconfigtype);
    const hidePageUrl = isPageUrlHidden(configType);
    const [SubmitButton, setSubmitButton] = useState((rowid) ? 'UPDATE RECORD' : 'SAVE RECORD');
    const [Notification, setNotification] = useState({ status: (rowid) ? true : false, message: 'Please wait while we fetch your record' })
    const [DisableSubmitButton, setDisableSubmitButton] = useState(false);
    const [Show, setShow] = useState(false);
    const [ConfigMode, setConfigMode] = useState('visual'); // 'visual' | 'raw'

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        reset
    } = useForm();

    const copyConfigToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text || '');
            toast.info('Config copied to clipboard');
        } catch (e) {
            toast.error('Unable to copy config to clipboard');
        }
    }

    const downloadConfigBackup = (text) => {
        const blob = new Blob([text || ''], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${urlconfigtype || 'config'}-backup-${Date.now()}.json`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }

    const updateChangeEvent = (value, field) => {
		field.onChange(value);
	}

    const hideModal = () => {
        setShow(false);
        setshowformmodalbox(false)
	}

    const onSubmit = async (data) => {
        setSubmitButton('LOADING...');
        setDisableSubmitButton(true);
        // Hidden Page Url: fixed value the website reads, else keep the record's value (or the default for new records)
        const pageUrl = !hidePageUrl
            ? data.pageUrl
            : (configType.fixedPageUrl || InputFields.pageUrl || configType.defaultPageUrl);
        let postData = {
            rowId: rowid,
            accountId: urlaccountid,
            statusId: data.statusId,
            pageUrl: pageUrl,
            pageConfig: JSON.stringify(data.pageConfig)
        }
        let apiCall = "";
        if(InputFields.rowId) {
            apiCall = await ajaxCall.patchCall(`${config.api.generic}/config/${urlconfigtype}`, postData, token);
        } else {
            apiCall = await ajaxCall.postCall(`${config.api.generic}/config/${urlconfigtype}`, postData, token);
        }
        if(apiCall && apiCall.status) {
            setSubmitButton((InputFields.rowId)?'UPDATE RECORD':'SAVE RECORD');
            toast.info('Record updated successfully');
            reset();
            setDisableSubmitButton(false);
            loadapidata(apidataparams); // refresh list page
        } else {
            setSubmitButton((InputFields.rowId)?'UPDATE RECORD':'SAVE RECORD');
            const message = apiCall && apiCall.context.message;
            toast.error((hidePageUrl && message === 'Page URL already available')
                ? `A ${configType.label} config already exists for this store. Update the existing record instead.`
                : (message || 'Something went wrong. Please try again :('));
            setDisableSubmitButton(false);
        }
    }

    // Get data from DB - Used for Edit option
    React.useEffect(() => {
        async function loadComponent() {
            setShow(true);
        }

        async function fetchData() {
            if (rowid) {
                let apiCall = await ajaxCall.getCall(`${config.api.generic}/config/row/${urlconfigtype}`, {
                    rowId: (rowid) ? rowid : 0,
                    accountId: urlaccountid
                }, token);
                if(apiCall && apiCall.status) {
                    let onLoadData = {
                        rowId: rowid,
                        accountId: apiCall.context.message.data[0].fk_org_account_id,
                        statusId: apiCall.context.message.data[0].status,
                        pageUrl: apiCall.context.message.data[0].page_url,
                        pageConfig: apiCall.context.message.data[0].page_config
                    }
                    setInputFields(onLoadData)
                    setNotification({ status: false })
                } else {
                    setNotification({ status: true, message: 'Sorry something went wrong while fetching record :(' })
                }
            } 
        }
        fetchData();
        loadComponent();
    }, []);

    return (
        <>
            <Modal show={Show} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
                <Modal.Header>
                    <Modal.Title>{rowid?'Update Config Record':'Add Config Record'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container>
                        <Row>
                            <form action="" method="" className="p-sm-1" onSubmit={handleSubmit(onSubmit)}>
                                <Row>
                                    {(Notification.status) ? <Col md="12" className="text-center">{Notification.message}</Col> : <>
                                        <Col md="12">
                                            <div className="text-left mt-3 vt-txt-dark-blue">
                                                <h4><b>Config Info</b></h4>
                                                <hr />
                                            </div>
                                        </Col>
                                        {hidePageUrl ? null : <Col md="6" className="mt-3">
                                            <label className="form-label fs-14rem fw-bold">Page Url</label>
                                            <input type="text" defaultValue={InputFields.pageUrl} className="form-control" name="pageUrl" {...register("pageUrl", { required: true, minLength: 1, maxLength: 122 })} disabled={disabledField} />
                                            <small className="form-text text-danger">
                                                {errors.pageUrl?.type === "required" && "Page Url is mandatory"}
                                            </small>
                                            <small className="form-text text-danger">
                                                {errors.pageUrl?.type === "minLength" && "Enter atleast 1 characters"}
                                            </small>
                                            <small className="form-text text-danger">
                                                {errors.pageUrl?.type === "maxLength" && "Only 122 characters allowed"}
                                            </small>
                                            {configType?.pageUrlHint ? <small className="form-text text-muted d-block">{configType.pageUrlHint}</small> : null}
                                        </Col>}
                                        <Col md="6" className="mt-3">
                                            <label className="form-label fs-14rem fw-bold">Status</label>
                                            <Controller
                                                name='statusId' 
                                                defaultValue={parseInt(InputFields.statusId)}
                                                rules={{required: true}}
                                                control={control} 
                                                render={({field}) => (
                                                    <select value={field.value || ""} className="form-control custom-select" name="statusId" onChange={(e) => {updateChangeEvent(e, field)}} disabled={disabledField}>
                                                        <option value=""> Choose...</option>
                                                        <option value={1}>Active</option>
                                                        <option value={0}>Disable</option>
                                                    </select>
                                                )}
                                            />	
                                            <small className="form-text text-danger">
                                                {errors.statusId?.type === "required" && "Status is mandatory"}
                                            </small>
                                        </Col>
                                        <Col md="12" className="mt-3">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <label className="form-label fs-14rem fw-bold mb-0">Config</label>
                                            </div>
                                            {urlconfigtype === 'notification-bar-config' ? (
                                                <p className="text-muted small mb-2">
                                                    One active record per store. Example JSON:
                                                    <br />
                                                    <code>{`{"enabled":true,"intervalMs":4000,"slides":[{"text":"Free shipping!","url":"/products","externalUrl":false}]}`}</code>
                                                </p>
                                            ) : null}
                                            {urlconfigtype === 'listing-page-config' ? (
                                                <p className="text-muted small mb-2">
                                                    Settings for the website's products page. One active record per store.
                                                    Set <code>categoryFilter</code> / <code>priceFilter</code> to <code>false</code> to hide that filter.
                                                    Without an active record, both filters are shown.
                                                </p>
                                            ) : null}
                                            {urlconfigtype === 'menu-config' ? (
                                                <p className="text-muted small mb-2">
                                                    Full-width menu under the website header. One active record per store.
                                                    Each item has <code>label</code>, <code>url</code> and optional <code>children</code> (sub-menu).
                                                    A child can have its own <code>children</code>, shown as a column of links.
                                                    Set <code>"categories": true</code> on an item to fill its sub-menu from your categories,
                                                    and <code>"externalUrl": true</code> to open a link in a new tab.
                                                    Without an active menu config, the menu shows your categories.
                                                </p>
                                            ) : null}
                                            <Controller
                                                name='pageConfig'
                                                defaultValue={InputFields.pageConfig}
                                                rules={{ required: true, minLength: 5 }}
                                                control={control}
                                                render={({ field }) => {
                                                    let parsedValue = null;
                                                    let parseError = null;
                                                    try {
                                                        parsedValue = field.value ? JSON.parse(field.value) : {};
                                                    } catch (e) {
                                                        parseError = e.message;
                                                    }
                                                    return (
                                                        <>
                                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                                                <div className="btn-group" role="group">
                                                                    <button type="button" className={`btn btn-sm ${ConfigMode === 'visual' ? 'btn-primary text-white' : 'btn-outline-primary'}`} onClick={() => setConfigMode('visual')} disabled={!!parseError}>Visual Builder</button>
                                                                    <button type="button" className={`btn btn-sm ${ConfigMode === 'raw' ? 'btn-primary text-white' : 'btn-outline-primary'}`} onClick={() => setConfigMode('raw')}>Raw JSON</button>
                                                                </div>
                                                                <div className="d-flex gap-2">
                                                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyConfigToClipboard(field.value)}>
                                                                        <FontAwesomeIcon icon={faCopy} /> Copy
                                                                    </button>
                                                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => downloadConfigBackup(field.value)}>
                                                                        <FontAwesomeIcon icon={faDownload} /> Backup
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {ConfigMode === 'visual' ? (
                                                                parseError ? (
                                                                    <div className="alert alert-warning fs-12rem py-2">Config isn't valid JSON yet ({parseError}). Fix it in Raw JSON mode first.</div>
                                                                ) : (
                                                                    <div className="border rounded p-2" style={{ maxHeight: '460px', overflowY: 'auto', background: '#f8f9fa' }}>
                                                                        <JsonNodeEditor
                                                                            value={parsedValue}
                                                                            disabled={disabledField}
                                                                            onChange={(newVal) => field.onChange(JSON.stringify(newVal, null, 2))}
                                                                        />
                                                                    </div>
                                                                )
                                                            ) : (
                                                                <textarea rows={14} className="form-control" value={field.value || ''} disabled={disabledField} onChange={(e) => field.onChange(e.target.value)}></textarea>
                                                            )}
                                                        </>
                                                    )
                                                }}
                                            />
                                            <small className="form-text text-danger">
                                                {errors.pageConfig?.type === "required" && "Config is mandatory"}
                                            </small>
                                            <small className="form-text text-danger">
                                                {errors.pageConfig?.type === "minLength" && "Enter atleast 5 characters"}
                                            </small>
                                        </Col>
                                        {(orginfo.roleId === 1 || orginfo.roleId === 2) ? <Col md="12" className="text-end">
                                            <Button variant="primary" type="submit" disabled={DisableSubmitButton} className="vt-primary-btn border-0 text-end text-white mt-4 fs-14rem">{SubmitButton}</Button>
                                        </Col> : null }
                                    </>}
                                </Row>
                            </form>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" className="fs-14rem" onClick={hideModal}>Close</Button>
                </Modal.Footer>
            </Modal>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    )
}

export default FormModal;